import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTicketAttachments } from '../../hooks/useTicketAttachments';
import { useTicketComments } from '../../hooks/useTicketComments';
import { Ticket } from '../../types/ticket';
import TicketStatusBadge from './TicketStatusBadge';
import TicketPriorityBadge from './TicketPriorityBadge';
import { Priority, Status } from '../../types';
import { useAuthContext } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { formatProfilePictureUrl } from '../../utils/imageUtils';
import { useTranslation } from 'react-i18next';

interface TicketListProps {
  tickets: Ticket[];
  currentPage: number;
  onTicketClick: (ticketId: number) => void;
}

export default function TicketList({ tickets, currentPage }: TicketListProps) {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { theme } = useTheme();
  const { attachmentCounts } = useTicketAttachments(tickets);
  const { commentCounts } = useTicketComments(tickets);
  const { t } = useTranslation();
  
  // Debug: Log attachment counts when they change
  React.useEffect(() => {
    if (Object.keys(attachmentCounts).length > 0) {
      console.log('Attachment counts loaded:', attachmentCounts);
    }
  }, [attachmentCounts]);

  const handleTicketClick = (ticketId: number) => {
    navigate(`/tickets/${ticketId}`, { 
      state: { 
        page: currentPage,
        from: '/tickets'
      } 
    });
  };

  return (
    <div className="relative">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tickets.map((ticket) => {
          const isAssignedToMe = ticket.assigned_to_id === user?.id;

          return (
            <div
              key={ticket.id}
              onClick={() => handleTicketClick(ticket.id)}
              className={`relative rounded-lg hover:shadow-2xl transition-all duration-300 cursor-pointer ${
                theme === 'dark' 
                  ? isAssignedToMe 
                    ? 'bg-gray-800 shadow-lg border border-blue-500' 
                    : 'bg-gray-800 shadow border border-gray-700' 
                  : isAssignedToMe 
                    ? 'bg-white shadow-lg border border-blue-500' 
                    : 'bg-white shadow'
              }`}
            >
              {/* Binder Holes */}
              <div className="absolute left-4 top-0 bottom-0 flex flex-col justify-around pointer-events-none">
                <div className={`w-2 h-2 rounded-full border ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600' 
                    : 'bg-gray-200 border-gray-300'
                }`}></div>
                <div className={`w-2 h-2 rounded-full border ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600' 
                    : 'bg-gray-200 border-gray-300'
                }`}></div>
                <div className={`w-2 h-2 rounded-full border ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600' 
                    : 'bg-gray-200 border-gray-300'
                }`}></div>
              </div>

              {/* Pin for Assigned Tickets */}
              {isAssignedToMe && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
                  <div className={`w-4 h-8 relative ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                    {/* Paper Clip Shape */}
                    <div className="absolute inset-x-0 top-0 bottom-0">
                      {/* Outer loop */}
                      <div className={`absolute inset-0 border-2 rounded-full transform rotate-45 ${
                        theme === 'dark' ? 'border-blue-400' : 'border-blue-600'
                      }`} style={{ borderRadius: '40% 40% 0 0' }}></div>
                      {/* Inner loop */}
                      <div className={`absolute inset-0 border-2 rounded-full transform rotate-45 scale-75 translate-y-0.5 ${
                        theme === 'dark' ? 'border-blue-400' : 'border-blue-600'
                      }`} style={{ borderRadius: '40% 40% 0 0' }}></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Main Content */}
              <div className="pl-10 pr-4 pt-4 pb-4">
                {/* Header */}
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs font-medium flex-shrink-0 ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>#{ticket.id}</span>
                        <h3 className={`text-sm font-medium truncate ${
                          theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                        }`}>
                          {ticket.title}
                        </h3>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <TicketStatusBadge status={ticket.status as Status} />
                    </div>
                  </div>
                  

                  <div className="flex items-center gap-2">
                    <TicketPriorityBadge priority={ticket.priority as Priority} />
                    {ticket.type_name && (
                      <span className={`px-2 py-1 text-xs rounded-md ${
                        theme === 'dark'
                          ? 'bg-gray-700 text-gray-300'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {ticket.type_name}
                      </span>
                    )}
                  </div>
                </div>

                {/* Description */}
                <p className={`mt-4 text-sm line-clamp-2 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {ticket.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-1 mt-4">
                  {ticket.source_name && (
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      theme === 'dark'
                        ? 'bg-purple-900/30 text-purple-400'
                        : 'bg-purple-50 text-purple-700'
                    }`}>
                      {ticket.source_name}
                    </span>
                  )}
                  {ticket.category_name && (
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      theme === 'dark'
                        ? 'bg-indigo-900/30 text-indigo-400'
                        : 'bg-indigo-50 text-indigo-700'
                    }`}>
                      {ticket.category_name}
                    </span>
                  )}
                  {!ticket.is_public && (
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      theme === 'dark'
                        ? 'bg-yellow-900/30 text-yellow-400'
                        : 'bg-yellow-50 text-yellow-700'
                    }`}>
                      Private
                    </span>
                  )}
                  {/* Always show attachment icon, with count if available */}
                  <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${
                    theme === 'dark'
                      ? 'bg-teal-900/30 text-teal-400'
                      : 'bg-teal-50 text-teal-700'
                  }`}>
                    <i className="fas fa-paperclip"></i>
                    {attachmentCounts[ticket.id] || '0'}
                  </span>
                  
                  {/* Always show comment icon, with count if available */}
                  <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${
                    theme === 'dark'
                      ? 'bg-blue-900/30 text-blue-400'
                      : 'bg-blue-50 text-blue-700'
                  }`}>
                    <i className="fas fa-comment"></i>
                    {commentCounts[ticket.id] || '0'}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1 mt-2">
                  {Array.isArray(ticket.tags) && ticket.tags.length > 0 && (
                    <>
                      {ticket.tags.map((tag, index) => (
                        <span
                          key={index}
                          className={`px-1 py-0.25 text-xs rounded-full ${
                            theme === 'dark'
                              ? `bg-gray-700 text-gray-300`
                              : `bg-gray-100 text-gray-700`
                          }`}
                        >
                          {typeof tag === 'object' && tag !== null ? tag.name : tag}
                        </span>
                      ))}
                    </>
                  )}
                </div>

                {/* Tear Line */}
                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-dashed border-gray-300"></div>
                  </div>
                </div>

                {/* Customer Info */}
                <div className={`flex items-center gap-3 p-2 rounded-lg border ${
                  theme === 'dark'
                    ? 'bg-gray-700/50 border-gray-600'
                    : 'bg-gray-50 border-gray-100'
                }`}>
                  <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                    theme === 'dark'
                      ? 'bg-blue-900/50 text-blue-300 ring-1 ring-blue-400/50'
                      : 'bg-blue-100 text-blue-600'
                  }`}>
                    {ticket['customer.full_name']?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                    }`}>
                      {ticket['customer.full_name']}
                    </p>
                    {ticket['customer.phone'] && (
                      <p className={`text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {ticket['customer.phone']}
                      </p>
                    )}
                  </div>
                </div>

                {/* Footer with Timestamps and Avatars */}
                <div className="mt-5 flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-4">
                    {ticket.created_by_id && (
                      <div className="flex items-center gap-2">
                        <div 
                          className={`h-6 w-6 rounded-full relative overflow-hidden ${
                            theme === 'dark'
                              ? 'ring-1 ring-green-400/50'
                              : ''
                          }`}
                          title={t('tickets.createdBy') + ': ' + (ticket['createdBy.fullName'] || t('common.unknown', 'Unknown'))}
                        >
                          {/* Profile picture with fallback to initials */}
                          {(ticket['createdBy.pfp_url'] || (ticket.created_by && ticket.created_by.pfp_url)) ? (
                            <img 
                              src={formatProfilePictureUrl(ticket['createdBy.pfp_url'] || (ticket.created_by && ticket.created_by.pfp_url) || '')}
                              alt={ticket['createdBy.fullName'] || (ticket.created_by && ticket.created_by.full_name) || 'Unknown'}
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                // Fallback to initials if image fails to load
                                const imgElement = e.target as HTMLImageElement;
                                imgElement.style.display = 'none';
                                const nextElement = imgElement.nextElementSibling as HTMLElement;
                                if (nextElement) {
                                  nextElement.style.display = 'flex';
                                }
                              }}
                            />
                          ) : null}
                          <div 
                            className={`${(ticket['createdBy.pfp_url'] || (ticket.created_by && ticket.created_by.pfp_url)) ? 'hidden' : 'flex'} h-full w-full items-center justify-center font-semibold text-xs ${
                              theme === 'dark'
                                ? 'bg-green-900/50 text-green-300'
                                : 'bg-green-100 text-green-600'
                            }`}
                          >
                            <span>
                              {(ticket['createdBy.fullName'] || 
                                (ticket.created_by && ticket.created_by.full_name) || 
                                'U')[0].toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-gray-400">{t('tickets.created', 'Created')}</span>
                          <span>{new Date(ticket.created_at).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}</span>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span className="text-gray-400">{t('tickets.updated', 'Updated')}</span>
                      <span>{new Date(ticket.updated_at).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}</span>
                    </div>
                  </div>
                  {ticket.assigned_to_id && (
                    <div 
                      className={`h-6 w-6 rounded-full relative overflow-hidden ${
                        theme === 'dark'
                          ? 'ring-1 ring-green-400/50'
                          : ''
                      }`}
                      title={t('tickets.assignedTo') + ': ' + (ticket['assignedTo.fullName'] || t('common.unknown', 'Unknown'))}
                    >
                      {/* Profile picture with fallback to initials */}
                      {(ticket['assignedTo.pfp_url'] || (ticket.assigned_to && ticket.assigned_to.pfp_url)) ? (
                        <img 
                          src={formatProfilePictureUrl(ticket['assignedTo.pfp_url'] || (ticket.assigned_to && ticket.assigned_to.pfp_url) || '')}
                          alt={ticket['assignedTo.fullName'] || (ticket.assigned_to && ticket.assigned_to.full_name) || 'Unknown'}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            // Fallback to initials if image fails to load
                            const imgElement = e.target as HTMLImageElement;
                            imgElement.style.display = 'none';
                            const nextElement = imgElement.nextElementSibling as HTMLElement;
                            if (nextElement) {
                              nextElement.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      <div 
                        className={`${(ticket['assignedTo.pfp_url'] || (ticket.assigned_to && ticket.assigned_to.pfp_url)) ? 'hidden' : 'flex'} h-full w-full items-center justify-center font-semibold text-xs ${
                          theme === 'dark'
                            ? 'bg-green-900/50 text-green-300'
                            : 'bg-green-100 text-green-600'
                        }`}
                      >
                        <span>
                          {(ticket['assignedTo.fullName'] || 
                            (ticket.assigned_to && ticket.assigned_to.full_name) || 
                            'U')[0].toUpperCase()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
